import { BrandLockup } from "../components/BrandLockup";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { TESTFLIGHT_LINK } from "../lib/appLink";
import { JOIN_BASE } from "../lib/invite";

/**
 * AN INVITATION LINK, OPENED — ON THE PHONE, AS A DOOR TO THE APP AND NOTHING ELSE.
 *
 * 🔴 HIS DIRECTION, 2026-09-22 ~23:45, after his wife opened one: *"why go in the web app for it?
 * Just download the app with a link and then add all their details, then when done, they will be
 * instantly linked with that coach, and no need to select the engine or coach."* The previous page
 * asked her to sign in or create an account ON THE WEB — and gave her no button to do either — then
 * to accept, then to install. It is now ONE action: get the app. The account, her own name and the
 * link to the coach all happen inside the app.
 *
 * 🔴 THE ONE THING APPLE WILL NOT DO, AND HOW IT IS CARRIED: an install does not pass a link through
 * to the app. So "Get the app" writes this invitation's link to the clipboard first (a tap is the
 * user gesture the clipboard requires), and the app reads it on its first launch — the system asks
 * once whether Coachwork may paste. An app that is ALREADY installed is opened straight into the
 * invitation by `coachwork://join/<token>`, the scheme the app already registers.
 *
 * ⚠️ PEEKING IS STILL THE ONLY THING DONE HERE. `peek_coach_invitation` is callable by `anon` and
 * returns the coach's name and the expiry; `claim_coach_invitation` stays `authenticated` only and
 * is called by the app after sign-up. Looking is not taking.
 */
export default function Join({ token }: { token: string }) {
  const [state, setState] = useState<"looking" | "found" | "gone">("looking");
  const [coach, setCoach] = useState("");
  const [note, setNote] = useState<string | null>(null);
  /* ⚠️ A ROUTE THAT MOVES FOCUS NOWHERE LEAVES A KEYBOARD OR SCREEN-READER USER STANDING ON THE
     LINK THEY JUST FOLLOWED. Every route moves focus to its heading; a gate counts the ones that
     do not. It matters most here: this page is opened by somebody who has never seen the product,
     and the heading is the entire explanation. */
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [state]);

  useEffect(() => {
    let alive = true;
    supabase
      .rpc("peek_coach_invitation", { p_token: token })
      .then(({ data, error }) => {
        if (!alive) return;
        // ⚠️ An invalid, used and expired token all arrive here as ZERO ROWS, and the page says
        // one thing for all three — distinguishing them would let somebody probe which exist.
        const row = Array.isArray(data) ? data[0] : data;
        if (error || !row) { setState("gone"); return; }
        setCoach(String(row.coach_name || "Your coach").trim());
        setState("found");
      });
    return () => { alive = false; };
  }, [token]);

  async function getTheApp() {
    // The canonical link, not `location.href`: the app matches `coachwork.io/join/<token>`, and a
    // copy carrying a hash-router prefix or a tracking suffix would be one it cannot read.
    try {
      await navigator.clipboard.writeText(JOIN_BASE + token);
    } catch {
      // Not fatal and not silent: the app can still be opened from this link after installing.
      setNote("After installing, come back and tap “I already have the app”.");
    }
    window.location.href = TESTFLIGHT_LINK;
  }

  if (state === "looking") {
    return <main id="main" className="join-page"><p className="ht-callout">Looking…</p></main>;
  }

  if (state === "gone") {
    return (
      <main id="main" className="join-page">
        <BrandLockup size={28} />
        <h1 className="ht-title" ref={heading} tabIndex={-1}>This invitation is no longer open</h1>
        <p className="ht-body">
          It may have been used already, or it may have expired. Ask your coach for a new link.
        </p>
      </main>
    );
  }

  return (
    <main id="main" className="join-page">
      {/* PRO WEB STEP 7 — the phone's lockup, unchanged (his 2026-09-26 word): the invited person has never seen this site,
          and the name they are about to download is the first thing that should say it is ours. */}
      <BrandLockup size={28} />
      <h1 className="ht-title" ref={heading} tabIndex={-1}>
        {coach} invited you to train with them
      </h1>
      <p className="ht-body">
        Get the Coachwork app and sign up. You’ll be connected to {coach} as soon as you finish.
      </p>

      <div className="join-actions">
        <button className="ht-button ht-button-primary join-primary" onClick={() => void getTheApp()}>
          Get the app
        </button>
        <a className="ht-button join-secondary" href={`coachwork://join/${token}`}>
          I already have the app
        </a>
      </div>

      {note !== null && <p className="ht-body join-note" role="status">{note}</p>}
    </main>
  );
}
